import React, { useEffect, useMemo } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import type { U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import type { U2103UserFormExtendedHandlers } from "../../../U2103UserForm";
import { NS } from "../../../../U2103";
import { type U2103UserAffiliation } from "../../../../U2103Service";
import { format } from "date-fns";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";

// Typage des props du composant
interface U2103UserOrganizationsProps {
  u2103UserFormHandlers: U2103UserFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des organisations
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      name: "organizationName",
      label: t("lblOrganizationName", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "createdAt",
      label: t("lblCreatedAt", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[150px]",
      type: "date",
      align: "left",
      display: (_column: U2000TableColumn, value: string) => {
        if (!value) return <span className="text-gray-400">—</span>;
        try {
          return <span>{format(new Date(value), "yyyy-MM-dd")}</span>;
        } catch {
          return <span className="text-gray-400">—</span>;
        }
      },
    },
  ];
}

// Composant principal pour l'affichage des organisations affiliées (lecture seule)
const U2103UserOrganizationsBody: React.FC<U2103UserOrganizationsProps> = ({ u2103UserFormHandlers }) => {
  // Translation
  const { t } = useTranslation();

  const gridStateHandlers = useU2000GridStateHandlers<U2103UserAffiliation>();

  // Définition des colonnes avec useMemo pour optimiser les performances
  const columns = useMemo(() => getColumnsDef(t), [t]);

  // Transposer les données du formStateHandlers au gridStateHandlers pour les affiliations
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));

    // Initialiser les données du tableau avec des IDs temporaires si nécessaire
    const affiliations = u2103UserFormHandlers.getData?.()?.affiliations as U2103UserAffiliation[] | undefined;
    const affiliationsWithIds = Array.isArray(affiliations)
      ? affiliations.map((affiliation) => ({
          ...affiliation,
          id: affiliation.id || generateTemporaryId(),
        }))
      : [];
    gridStateHandlers.setData(affiliationsWithIds);
    gridStateHandlers.setTotalRows(affiliationsWithIds.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u2103UserFormHandlers, columns]);

  // Render
  return (
    <U2000DetailTable
      gridStateHandlers={gridStateHandlers}
      allowRowSelection={false}
      label={t("lblUserOrganization", {
        count: gridStateHandlers.getTotalRows(),
        ns: NS,
      })}
    />
  );
};

export default U2103UserOrganizationsBody;
